/**
 * useShellViewport
 * ────────────────
 * When the app is in portrait-mode on a small device (≤ 1024 px) the
 * CSS rotation trick in `.responsive-orientation-shell` swaps width ↔ height.
 *
 * Standard `window.innerWidth` / `window.innerHeight` still report the
 * *physical* (un-rotated) viewport though, so any JavaScript that calculates
 * sizes based on those values will get the wrong numbers.
 *
 * This hook:
 *  1. Detects whether the rotation is active.
 *  2. Exposes the *logical* (post-rotation) width / height as a simple object.
 *  3. Sets CSS custom properties `--shell-w-px` / `--shell-h-px` on `:root`
 *     so CSS can also use resolved pixel values when needed.
 *  4. Re-evaluates on resize and orientation change.
 *
 * On desktop (> 1024 px) or landscape, the values are identical to the
 * standard viewport — nothing changes.
 */
import { useState, useEffect, useCallback } from "react";

const BREAKPOINT = 1024;

const getIsPortraitSmall = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= BREAKPOINT && window.innerHeight > window.innerWidth;
};

const getLogicalSize = () => {
  if (typeof window === "undefined") return { width: 1024, height: 768 };

  const isRotated = getIsPortraitSmall();

  if (isRotated) {
    // Shell width  = physical height  (dvh)
    // Shell height = physical width   (dvw)
    return {
      width: window.innerHeight,
      height: window.innerWidth,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

const useShellViewport = () => {
  const [viewport, setViewport] = useState(getLogicalSize);

  const refresh = useCallback(() => {
    const next = getLogicalSize();
    setViewport((prev) => {
      if (prev.width === next.width && prev.height === next.height) return prev;
      return next;
    });

    // Also push resolved pixel values onto :root for CSS consumption
    const root = document.documentElement;
    root.style.setProperty("--shell-w-px", `${next.width}px`);
    root.style.setProperty("--shell-h-px", `${next.height}px`);
  }, []);

  useEffect(() => {
    refresh(); // initial

    window.addEventListener("resize", refresh);
    window.addEventListener("orientationchange", refresh);

    // screen.orientation API (more reliable on mobile)
    const screenOr = window.screen?.orientation;
    if (screenOr) {
      screenOr.addEventListener("change", refresh);
    }

    return () => {
      window.removeEventListener("resize", refresh);
      window.removeEventListener("orientationchange", refresh);
      if (screenOr) {
        screenOr.removeEventListener("change", refresh);
      }
    };
  }, [refresh]);

  return viewport; // { width, height }
};

export default useShellViewport;
export { getLogicalSize, getIsPortraitSmall };
